import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useConsultation } from "../context/ConsultationContext";
import { createConsultation } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const SERVICES = [
  { value: "mainland", label: "Mainland Company Formation" },
  { value: "freezone", label: "Freezone Company Formation" },
  { value: "visa", label: "Visa & Residency" },
  { value: "banking", label: "Corporate Bank Account" },
  { value: "other", label: "General Consultation" },
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "other",
  message: "",
};

export default function ConsultationDialog() {
  const { isOpen, close, defaultService } = useConsultation();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm({ ...initialForm, service: defaultService || "other" });
      setDone(false);
    }
  }, [isOpen, defaultService]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await createConsultation(form);
      setDone(true);
      toast.success("Thanks — we'll be in touch within 24 hours.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(", ")
        : detail || err?.message || "Something went wrong.";
      toast.error(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent
        data-testid="consultation-dialog"
        className="max-w-lg bg-[#002366] border border-white/10 text-white p-0 overflow-hidden"
      >
        <button
          data-testid="consultation-close"
          onClick={close}
          className="absolute top-4 right-4 z-10 w-8 h-8 inline-flex items-center justify-center rounded-sm border border-white/20 text-white/80 hover:text-white hover:bg-white/10"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {done ? (
          <div className="p-10 text-center">
            <CheckCircle2 className="w-14 h-14 text-[#6B8AFF] mx-auto" strokeWidth={1.5} />
            <DialogTitle className="mt-6 font-heading text-2xl md:text-3xl font-light tracking-tight">
              Thanks!
            </DialogTitle>
            <DialogDescription className="mt-3 text-slate-200/80">
              We'll be in touch within <span className="font-semibold text-white">24 hours</span>.
              Check your inbox — we've also sent a confirmation to <span className="font-semibold text-white">{form.email || "your email"}</span>.
            </DialogDescription>
            <button
              data-testid="consultation-dialog-close-done"
              onClick={close}
              className="btn-gold mt-8"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="p-8 md:p-10">
            <p className="overline text-[#6B8AFF]">Free Consultation</p>
            <DialogTitle className="mt-3 font-heading text-2xl md:text-3xl font-light tracking-tight">
              Tell us about your{" "}
              <span className="font-black text-[#6B8AFF]">vision</span>.
            </DialogTitle>
            <DialogDescription className="mt-3 text-sm text-slate-300/80">
              No obligations — just a 20-minute strategy call with a senior advisor.
            </DialogDescription>

            <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="consultation-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  testId="form-name"
                  label="Full Name"
                  value={form.name}
                  onChange={(v) => update("name", v)}
                  placeholder="Jane Doe"
                />
                <Field
                  testId="form-email"
                  label="Work Email"
                  type="email"
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  placeholder="jane@company.com"
                />
              </div>
              <Field
                testId="form-phone"
                label="Phone (with country code)"
                value={form.phone}
                onChange={(v) => update("phone", v)}
                placeholder="+971 50 000 0000"
              />

              <div>
                <label className="overline text-white/60 block mb-2">
                  Service of Interest
                </label>
                <Select
                  value={form.service}
                  onValueChange={(v) => update("service", v)}
                >
                  <SelectTrigger
                    data-testid="form-service"
                    className="w-full bg-white/5 border border-white/20 text-white rounded-sm h-11 focus:ring-[#3366FF]/40"
                  >
                    <SelectValue placeholder="Select service" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A1128] border border-white/10 text-white">
                    {SERVICES.map((s) => (
                      <SelectItem
                        key={s.value}
                        value={s.value}
                        className="focus:bg-white/10 focus:text-white"
                      >
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="overline text-white/60 block mb-2">
                  Message (optional)
                </label>
                <textarea
                  data-testid="form-message"
                  rows={3}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us what you want to build in the UAE…"
                  className="w-full rounded-sm bg-white/5 border border-white/20 text-white placeholder-white/40 px-3 py-2.5 focus:outline-none focus:border-[#6B8AFF]/50 focus:ring-2 focus:ring-[#3366FF]/20"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="form-submit"
                className="btn-gold w-full disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Request my consultation"}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>

              <p className="text-xs text-white/50 text-center">
                Your information stays private. We never share data with third parties.
              </p>
            </form>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="overline text-white/60 block mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-sm bg-white/5 border border-white/20 text-white placeholder-white/40 px-3 py-2.5 focus:outline-none focus:border-[#6B8AFF]/50 focus:ring-2 focus:ring-[#3366FF]/20"
      />
    </div>
  );
}
